import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'

import { Box } from '@mui/material'
import { CopyButton } from 'components/common/CopyButton'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspacePlan } from 'utils/hooks'

interface Props {
  /** Assignment ID to be used for tracking purposes from Client gallery flow */
  assignmentId?: string
  /** Property ID to be used for tracking purposes from Property flow */
  propertyId?: string
}

/**
 * @component Copy Gallery Link component shows confirmation text onClick and after 1500ms resets the state to default text,
 * for non subscribed users it is in disabled state with lock icon and tooltip encouraging user to subscribe
 * @example
 * <CopyGalleryLink />
 */
export const CopyGalleryLink: React.FC<Props> = ({ assignmentId, propertyId }) => {
  const {
    clientData,
    baseUserData,
    organizationData,
    currentUserWorkspace
  } = useUserData()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const { t } = useTranslation(['gallery'])

  return (
    <div>

      {/** CONTROLLED TOOLTIP FOR USERS WITH SUBSCRIPTION, CONFIRMING COPY ACTION */}
      {hasWorkspaceActiveSubscription &&
        <CopyButton
          type='secondary'
          buttonSize='md'
          copyText={window.location.href}
          textContent={t('link_copy')}
          copySuccessText={t('link_copy_success')}
          onCopy={() => {
            logAnalyticsEvent(AnalyticsEvent.USE_COPY_LINK, {
              userId: baseUserData?.id,
              userEmail: baseUserData?.email,
              organizationId: organizationData?.id,
              planId: currentUserWorkspace?.id,
              workspaces: clientData?.workspaces,
              ...(assignmentId && { assignmentId }),
              ...(propertyId && { propertyId, realtyId: propertyId })
            })
          }}
        />
      }

      {/** UNCONTROLLED TOOLTIP FOR SAAS UPGRADE */}
      {!hasWorkspaceActiveSubscription &&
        <MUITooltip
          content={t('link_copy_sass_upgrade')}
        >
          <Box onClick={() => externalNavigate(getPricingUrl(i18n.language))}>
            <MUIButton
              type="secondaryBorder"
              endIcon={<LockOutlinedIcon fontSize='large' />}
              disabled
            >
              {t('link_copy')}
            </MUIButton>
          </Box>
        </MUITooltip>
      }
    </div>
  )
}
